import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  Mail, 
  Lock, 
  User, 
  Building2, 
  Phone,
  ArrowRight,
  Eye,
  EyeOff
} from 'lucide-react';

const AuthLayout = ({ children }) => {
  const { t, language, changeLanguage, languages } = useLanguage();
  
  return (
    <div className="min-h-screen bg-emerald-950 flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-lime-400/10 via-emerald-950 to-emerald-950"></div>
        
        <div className="relative z-10">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-lime-400 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-emerald-950" />
            </div>
            <span className="text-2xl font-bold text-white font-serif">TradeFlow</span>
          </Link>
        </div>
        
        <div className="relative z-10 space-y-6">
          <h1 className="text-4xl lg:text-5xl font-serif font-medium text-white leading-tight">
            {t('authPage.sideTitle')}
          </h1>
          <p className="text-lg text-slate-400 max-w-md">
            {t('authPage.sideSubtitle')}
          </p>
          <div className="grid grid-cols-3 gap-6 pt-8">
            <div>
              <p className="text-3xl font-bold text-lime-400 font-mono">₽50M+</p>
              <p className="text-sm text-slate-500">{t('authPage.financed')}</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-lime-400 font-mono">200+</p>
              <p className="text-sm text-slate-500">{t('authPage.clients')}</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-lime-400 font-mono">{t('authPage.speedValue')}</p>
              <p className="text-sm text-slate-500">{t('authPage.speed')}</p>
            </div>
          </div>
        </div>
        
        <div className="relative z-10 flex items-center space-x-4">
          <Select value={language} onValueChange={changeLanguage}>
            <SelectTrigger className="w-20 bg-transparent border-emerald-800 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map((item) => (
                <SelectItem key={item.code} value={item.code}>{item.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        {children}
      </div>
    </div>
  );
};

const SocialButtons = () => {
  const { t } = useLanguage();
  
  const socialProviders = [
    { name: 'Google', icon: '🔍', disabled: true },
    { name: 'Yandex', icon: 'Я', disabled: true },
    { name: 'VK', icon: 'VK', disabled: true },
    { name: 'WeChat', icon: '微', disabled: true }
  ];
  
  return (
    <div className="space-y-4">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <Separator className="w-full" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-slate-500">{t('auth_social_login')}</span>
        </div>
      </div>
      
      <div className="grid grid-cols-4 gap-2">
        {socialProviders.map((provider) => (
          <Button
            key={provider.name}
            variant="outline"
            className="relative opacity-50 cursor-not-allowed"
            disabled
            data-testid={`social-${provider.name.toLowerCase()}-btn`}
          >
            <span className="text-lg">{provider.icon}</span>
            <span className="absolute -top-2 -right-2 text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">
              {t('auth_coming_soon')}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
};

export const LoginPage = () => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success(t('authPage.welcome'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('authPage.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Card className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-100" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="lg:hidden flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-emerald-950 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-lime-400" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-serif">TradeFlow</span>
          </div>
          <CardTitle className="text-2xl font-serif">{t('auth_login_title')}</CardTitle>
          <CardDescription>{t('authPage.loginDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth_email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  className="pl-10 bg-slate-50 border-slate-200"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth_password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-slate-50 border-slate-200"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-emerald-950 hover:bg-emerald-900 text-white rounded-full py-6"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? t('msg_loading') : t('auth_submit_login')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>
          
          <SocialButtons />
          
          <p className="text-center text-sm text-slate-500">
            {t('auth_no_account')}{' '}
            <Link to="/register" className="text-emerald-600 hover:text-emerald-700 font-medium" data-testid="login-register-link">
              {t('nav_register')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export const RegisterPage = () => {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    company_name: '',
    phone: '',
    role: 'client_seller',
    language: 'ru'
  });

  const roles = [
    { value: 'client_buyer', label: t('role_client_buyer') },
    { value: 'client_seller', label: t('role_client_seller') },
    { value: 'agent', label: t('role_agent') },
    { value: 'partner_investor', label: t('role_partner_investor') },
    { value: 'partner_institution', label: t('role_partner_institution') }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('authPage.passwordsMismatch'));
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error(t('authPage.passwordTooShort'));
      return;
    }
    
    setLoading(true);
    try {
      const { confirmPassword, ...userData } = formData;
      await register(userData);
      toast.success(t('authPage.registerSuccess'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('authPage.registerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Card className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-100" data-testid="register-card">
        <CardHeader className="text-center pb-2">
          <div className="lg:hidden flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-emerald-950 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-lime-400" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-serif">TradeFlow</span>
          </div>
          <CardTitle className="text-2xl font-serif">{t('auth_register_title')}</CardTitle>
          <CardDescription>{t('authPage.registerDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">{t('auth_full_name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="full_name"
                    placeholder={t('authPage.fullNamePlaceholder')}
                    className="pl-10 bg-slate-50 border-slate-200"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="company_name">{t('auth_company')}</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="company_name"
                    placeholder={t('authPage.companyPlaceholder')}
                    className="pl-10 bg-slate-50 border-slate-200"
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    data-testid="register-company-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth_email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  className="pl-10 bg-slate-50 border-slate-200"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">{t('auth_phone')}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+7 (999) 123-45-67"
                  className="pl-10 bg-slate-50 border-slate-200"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="register-phone-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('auth_role')}</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="register-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth_password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-10 bg-slate-50 border-slate-200"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    data-testid="register-password-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('auth_confirm_password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-10 bg-slate-50 border-slate-200"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                type="button"
                className="text-sm text-slate-500 hover:text-slate-700"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="w-4 h-4 inline mr-1" /> : <Eye className="w-4 h-4 inline mr-1" />}
                {showPassword ? t('authPage.hidePasswords') : t('authPage.showPasswords')}
              </button>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-emerald-950 hover:bg-emerald-900 text-white rounded-full py-6"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? t('msg_loading') : t('auth_submit_register')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>
          
          <SocialButtons />
          
          <p className="text-center text-sm text-slate-500">
            {t('auth_have_account')}{' '}
            <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium" data-testid="register-login-link">
              {t('nav_login')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};
